import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import type { Materia, HorarioType } from 'models/materia_model';
import {
  getMaterias,
  createMateria,
  createAllMaterias,
  editMateria,
  editHorario,
  deleteMateria,
} from 'repositories/materia_repository';

interface MateriaState {
  materias: Materia[];
  materia: Materia | null;
  loading: boolean;
  error: string | null;
}

const initialState: MateriaState = {
  materias: [],
  materia: null,
  loading: false,
  error: null,
};

export const cargarMaterias = createAsyncThunk<Materia[], { cursoId: string; materiaFilter?: string; siglaFilter?: string }, { rejectValue: string }>(
  'materia/cargarMaterias',
  async (
    { cursoId, materiaFilter, siglaFilter },
    { rejectWithValue },
  ) => {
    try {
      return ((await getMaterias(cursoId, materiaFilter, siglaFilter)) ?? []) as Materia[];
    } catch {
      return rejectWithValue('Error al cargar materias');
    }
  },
);

export const agregarMateria = createAsyncThunk(
  'materia/agregarMateria',
  async ({ cursoId, data }: { cursoId: string; data: Materia }, { rejectWithValue }) => {
    try {
      await createMateria(cursoId, data);
    } catch {
      return rejectWithValue('Error al crear materia');
    }
  },
);

export const agregarTodosMaterias = createAsyncThunk(
  'materia/agregarTodosMaterias',
  async ({ cursoId, data }: { cursoId: string; data: Materia[] }, { rejectWithValue }) => {
    try {
      await createAllMaterias(cursoId, data);
    } catch {
      return rejectWithValue('Error al crear materias');
    }
  },
);

export const editarMateria = createAsyncThunk(
  'materia/editarMateria',
  async (
    { cursoId, data, materiaId }: { cursoId: string; data: Materia; materiaId: string },
    { rejectWithValue },
  ) => {
    try {
      await editMateria(cursoId, data, materiaId);
    } catch {
      return rejectWithValue('Error al editar materia');
    }
  },
);

export const editarHorario = createAsyncThunk(
  'materia/editarHorario',
  async (
    {
      cursoId,
      materiaId,
      fechaCambio,
      horarios,
    }: { cursoId: string; materiaId: string; fechaCambio: string; horarios: HorarioType[] },
    { rejectWithValue },
  ) => {
    try {
      await editHorario(cursoId, materiaId, fechaCambio, horarios);
    } catch {
      return rejectWithValue('Error al editar horario');
    }
  },
);

export const eliminarMateria = createAsyncThunk(
  'materia/eliminarMateria',
  async ({ cursoId, id }: { cursoId: string; id: string }, { rejectWithValue }) => {
    try {
      await deleteMateria(cursoId, id);
    } catch {
      return rejectWithValue('Error al eliminar materia');
    }
  },
);

export const materiaSlice = createSlice({
  name: 'materia',
  initialState,
  reducers: {
    changeMateria: (state, action: PayloadAction<Materia | null>) => {
      state.materia = action.payload;
    },
    setMaterias: (state, action: PayloadAction<Materia[]>) => {
      state.materias = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(cargarMaterias.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cargarMaterias.fulfilled, (state, action) => {
        state.loading = false;
        state.materias = action.payload;
      })
      .addCase(cargarMaterias.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { changeMateria, setMaterias } = materiaSlice.actions;
export default materiaSlice.reducer;
